import { ProgramNames } from '../../../models/programs/Programs'
import { PROVIDER_TYPES } from '../../../models/providers/Providers'
import { PROGRAM_TAXONOMY, ProgramCustomerPropertyId } from '../../common/constants/customerProgram'
import { TREATMENT_OPTIONS } from '../../common/constants/treatmentOptions'
import { LTSessionTypes } from '../../sessions/constants'
import { ConsentText, ProgramDisplayLabels } from '../messages'
import { ProgramConfig } from '../types'

export const COACHING_PROGRAM = {
  programName: ProgramNames.Coaching,
  blendedCareProgramId: '7ec82ca3-843f-4996-bdf9-49d8c7a35ed5',
  customerPropertyId: ProgramCustomerPropertyId.stressManagement,
  selfPayEligible: true,
  isCoachingProgram: true,

  displayLabels: {
    default: ProgramDisplayLabels.coaching,
    sessionSummary: 'coaching',
  },

  associatedProviderTypes: [PROVIDER_TYPES.COACH.lyraType],
  associatedProviderModalities: ['stressManagement'],

  canRescheduleAppointment: true,

  sessionTypeConfig: {
    sessionType: LTSessionTypes.SMP_SESSION,
    internationalSessionType: LTSessionTypes.INTL_SMP_SESSION,
    rateStrategy: 'lookupSessionRate',
  },

  directBookingParams: {
    clientele: PROGRAM_TAXONOMY.clientele.individual,
    treatment: PROGRAM_TAXONOMY.treatment.coaching,
    partner: PROGRAM_TAXONOMY.partner.blendedCare,
    offering: PROGRAM_TAXONOMY.offering.default,
  },

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.COACHING],
    isFullTreatmentOption: true,
  },

  consents: [
    {
      consentText: ConsentText.coachingConsent,
      consentUrl: 'https://www.lyrahealth.com/bcc-agreement',
      showConsentIf: (options) => options?.isInternational === false,
    },
    {
      consentText: ConsentText.coachingConsent,
      consentUrl: 'https://www.lyrahealth.com/bcc-agreement-2',
      showConsentIf: (options) => options?.isInternational === true,
    },
  ],

  noteContentMetadataName: {
    INITIAL_NOTE: 'bccInitialNote',
    SESSION_NOTE: 'bccSessionNote',
    GENERAL_NOTE: 'baseGeneralNote',
    REFERRAL_NOTE: 'baseReferralNote',
    OLD_INITIAL_NOTE: 'coachingInitialNote',
    OLD_SESSION_NOTE: 'coachingSessionNote',
    OLD_GENERAL_NOTE: 'coachingGeneralNote',
    OLD_REFERRAL_NOTE: 'coachingReferralNote',
  },
} satisfies ProgramConfig

export const COACHING_FOR_PARENTS_PROGRAM = {
  programName: ProgramNames.CoachingForParents,
  blendedCareProgramId: '5ee0996e-c7cd-46d1-b009-462f6379cbd5',
  customerPropertyId: ProgramCustomerPropertyId.coachingForParents,
  isCoachingProgram: true,

  displayLabels: {
    default: ProgramDisplayLabels.coachingForParents,
    careType: ProgramDisplayLabels.coaching,
    short: 'CFP',
    sessionSummary: 'coaching',
  },

  canRescheduleAppointment: true,
  selfPayEligible: true,

  sessionTypeConfig: {
    sessionType: LTSessionTypes.COACHING_FOR_PARENTS_SESSION,
    rateStrategy: 'lookupSessionRate',
  },

  directBookingParams: {
    clientele: PROGRAM_TAXONOMY.clientele.individual,
    treatment: PROGRAM_TAXONOMY.treatment.coaching,
    partner: PROGRAM_TAXONOMY.partner.blendedCare,
    offering: PROGRAM_TAXONOMY.offering.parenting,
  },

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.COACHING_FOR_PARENTS],
    isFullTreatmentOption: true,
  },

  associatedProviderTypes: [PROVIDER_TYPES.COACH.lyraType],
  associatedProviderModalities: ['coachingForParents'],

  consents: [
    {
      consentText: ConsentText.coachingConsent,
      consentUrl: 'https://www.lyrahealth.com/bcc-agreement',
      showConsentIf: (options) => options?.isInternational === false,
    },
    {
      consentText: ConsentText.coachingConsent,
      consentUrl: 'https://www.lyrahealth.com/bcc-agreement-2',
      showConsentIf: (options) => options?.isInternational === true,
    },
  ],

  noteContentMetadataName: {
    INITIAL_NOTE: 'bccInitialNote',
    SESSION_NOTE: 'bccSessionNote',
    GENERAL_NOTE: 'baseGeneralNote',
    REFERRAL_NOTE: 'baseReferralNote',
  },
} satisfies ProgramConfig

export const NEURODIVERSITY_SKILLS_COACHING_PROGRAM = {
  programName: ProgramNames.NeurodiversitySkillsCoaching,
  blendedCareProgramId: '394a8442-6e91-4423-98da-0c4c49e4b847',
  customerPropertyId: ProgramCustomerPropertyId.neurodiversitySkillsCoaching,
  isCoachingProgram: true,

  displayLabels: {
    default: ProgramDisplayLabels.neurodiversitySkillsCoaching,
    careType: ProgramDisplayLabels.neurodiversitySkillsCoaching,
    short: 'NSC',
    clientProfile: 'NSC',
    sessionSummary: 'coaching',
  },

  associatedProviderTypes: [PROVIDER_TYPES.COACH.lyraType],
  associatedProviderModalities: ['stressManagement'],

  canRescheduleAppointment: true,
  selfPayEligible: true,

  sessionTypeConfig: {
    sessionType: LTSessionTypes.NSC_SESSION,
    rateStrategy: 'lookupSessionRate',
  },

  directBookingParams: {
    clientele: PROGRAM_TAXONOMY.clientele.individual,
    treatment: PROGRAM_TAXONOMY.treatment.coaching,
    partner: PROGRAM_TAXONOMY.partner.blendedCare,
    offering: PROGRAM_TAXONOMY.offering.neurodiversitySkills,
  },

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.NEURODIVERSITY_SKILLS_COACHING],
    isFullTreatmentOption: true,
  },

  consents: [
    {
      consentText: ConsentText.neurodiversitySkillsCoachingConsent,
      consentUrl: 'https://www.lyrahealth.com/nsc-agreement',
      showConsentIf: () => true,
    },
  ],

  // TODO: Replace with NSC-specific notes once they are created
  noteContentMetadataName: {
    INITIAL_NOTE: 'bccInitialNote',
    SESSION_NOTE: 'bccSessionNote',
    GENERAL_NOTE: 'baseGeneralNote',
    REFERRAL_NOTE: 'baseReferralNote',
  },
} satisfies ProgramConfig

export const CAREGIVER_TRAINING_FOR_NEURO_PROGRAM = {
  programName: ProgramNames.CaregiverTrainingForNeuro,
  blendedCareProgramId: '48776a93-5203-4961-8020-02f1354ea0a3',
  customerPropertyId: ProgramCustomerPropertyId.caregiverTrainingForNeuro,
  isCoachingProgram: true,
  shouldHideClientGoals: true,

  displayLabels: {
    default: ProgramDisplayLabels.caregiverTrainingForNeuro,
    careType: ProgramDisplayLabels.caregiverTrainingForNeuro,
    short: 'CGT',
    clientProfile: 'NSC',
    sessionSummary: 'coaching',
  },

  associatedProviderTypes: [PROVIDER_TYPES.COACH.lyraType],
  associatedProviderModalities: ['stressManagement'],

  canRescheduleAppointment: true,
  selfPayEligible: true,

  sessionTypeConfig: {
    sessionType: LTSessionTypes.CGT_SESSION,
    rateStrategy: 'lookupSessionRate',
  },

  directBookingParams: {
    clientele: PROGRAM_TAXONOMY.clientele.individual,
    treatment: PROGRAM_TAXONOMY.treatment.coaching,
    partner: PROGRAM_TAXONOMY.partner.blendedCare,
    offering: PROGRAM_TAXONOMY.offering.caregiverTrainingForNeuro,
  },

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.CAREGIVER_TRAINING_FOR_NEURO],
    isFullTreatmentOption: true,
  },

  consents: [
    {
      consentText: ConsentText.caregiverTrainingForNeuroConsent,
      consentUrl: 'https://www.lyrahealth.com/cgt-agreement/',
      showConsentIf: () => true,
    },
  ],

  // TODO: Replace with CGT-specific notes once they are created
  noteContentMetadataName: {
    INITIAL_NOTE: 'bccInitialNote',
    SESSION_NOTE: 'bccSessionNote',
    GENERAL_NOTE: 'baseGeneralNote',
    REFERRAL_NOTE: 'baseReferralNote',
  },
} satisfies ProgramConfig

export const PERFORMANCE_COACHING_PROGRAM = {
  programName: ProgramNames.PerformanceCoaching,
  blendedCareProgramId: null,
  customerPropertyId: ProgramCustomerPropertyId.stressManagement,
  selfPayEligible: true,
  isCoachingProgram: true,
  shouldHideClientGoals: true,

  displayLabels: {
    default: ProgramDisplayLabels.performanceCoaching,
    sessionSummary: 'coaching',
  },

  associatedProviderTypes: [PROVIDER_TYPES.COACH.lyraType],
  associatedProviderModalities: ['stressManagement'],

  canRescheduleAppointment: true,

  directBookingParams: {
    clientele: PROGRAM_TAXONOMY.clientele.individual,
    treatment: PROGRAM_TAXONOMY.treatment.coaching,
    partner: PROGRAM_TAXONOMY.partner.blendedCare,
    offering: PROGRAM_TAXONOMY.offering.performance,
  },

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.PERFORMANCE_COACHING],
    isFullTreatmentOption: false,
  },

  consents: [
    {
      consentText: ConsentText.coachingConsent,
      consentUrl: 'https://www.lyrahealth.com/bcc-agreement',
      showConsentIf: (options) => options?.isInternational === false,
    },
    {
      consentText: ConsentText.coachingConsent,
      consentUrl: 'https://www.lyrahealth.com/bcc-agreement-2',
      showConsentIf: (options) => options?.isInternational === true,
    },
  ],
} satisfies ProgramConfig

export const SINGLE_SESSION_COACHING_PROGRAM = {
  programName: ProgramNames.SingleSessionCoaching,
  blendedCareProgramId: 'deb574e4-6aa3-4b47-bd21-d8be3ba5d42b',
  customerPropertyId: ProgramCustomerPropertyId.guidedSelfCareEnabled,
  isCoachingProgram: true,

  displayLabels: {
    default: ProgramDisplayLabels.guidedSelfCare,
    clientProfile: 'Guided self-care coaching',
    short: 'GSC',
    sessionSummary: 'coaching',
  },

  associatedProviderTypes: [PROVIDER_TYPES.COACH.lyraType],
  associatedProviderModalities: ['singleSessionCoaching'],

  canRescheduleAppointment: true,
  selfPayEligible: false,

  sessionTypeConfig: {
    sessionType: LTSessionTypes.SSC_SESSION,
    rateStrategy: 'lookupSessionRate',
  },

  directBookingParams: {
    clientele: PROGRAM_TAXONOMY.clientele.individual,
    treatment: PROGRAM_TAXONOMY.treatment.coaching,
    partner: PROGRAM_TAXONOMY.partner.blendedCare,
    offering: PROGRAM_TAXONOMY.offering.singleSession,
  },

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.GUIDED_SELF_CARE],
    isFullTreatmentOption: true,
  },

  consents: [
    {
      consentText: ConsentText.guidedSelfCareConsent,
      consentUrl: 'https://www.lyrahealth.com/gsc-agreement',
      showConsentIf: () => true,
    },
  ],

  noteContentMetadataName: {
    INITIAL_NOTE: 'sscInitialNote',
    SESSION_NOTE: 'sscSessionNote',
    GENERAL_NOTE: 'baseGeneralNote',
    REFERRAL_NOTE: 'baseReferralNote',
    OLD_INITIAL_NOTE: 'singleSessionCoachingInitialNote',
    OLD_SESSION_NOTE: 'singleSessionCoachingSessionNote',
    OLD_GENERAL_NOTE: 'singleSessionCoachingGeneralNote',
    OLD_REFERRAL_NOTE: 'singleSessionCoachingReferralNote',
  },
} satisfies ProgramConfig
